/**
 * CM WP Starter — main front-end script entry point.
 * Compiled by gulp to assets/dist/main.min.js
 */

document.addEventListener( 'DOMContentLoaded', function () {
	initScrollAnimations();
	initNavDropdownHover();
	initGalleryRails();
	initHeroVideos();
	initCatalogForms();
	initPriceRanges();
	initProductGalleries();
	initStickyBuyBars();
	initQuantitySteppers();
	initSearchModal();
} );

/**
 * Initialise AOS.
 *
 * The attributes themselves live on the CONTENT of a section, never on the
 * <section> element — see SECTION-CONTRACT.md → "Where data-aos goes". That is
 * a markup rule; what is set here is the behaviour that rule assumes.
 *
 * `once` is the important one, and it is not the default. Out of the box AOS
 * re-hides an element the moment it leaves the viewport and plays the entrance
 * again on the way back — so scrolling up a page re-animates everything already
 * read. An entrance is a first impression; it happens once.
 *
 * `disable` is the reduced-motion gate. AOS has no media query of its own and
 * starts every target at opacity 0, so without this a visitor who has asked for
 * less motion gets the whole page faded and slid into place anyway. Returning
 * true makes AOS skip initialising entirely and leave every element painted
 * where it belongs — the same call _marquee-band.scss and initHeroVideos() make
 * for their own motion.
 *
 * The remaining values are the defaults stated rather than inherited: 600ms is a
 * step up from the 400 default because the travel is now a block inside a band
 * rather than the whole band, and a short distance over a short duration reads
 * as a twitch. `offset` is how far above the fold a target fires, in pixels.
 */
function initScrollAnimations() {
	if ( typeof AOS === 'undefined' ) {
		return;
	}

	AOS.init( {
		once: true,
		duration: 600,
		easing: 'ease-out-cubic',
		offset: 80,
		disable: function () {
			return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
		},
	} );
}

/**
 * Put the caret in the search field when the search modal opens.
 *
 * Bootstrap moves focus to the dialog itself, which is correct for a modal in
 * general but wrong for this one — there is a single field and typing in it is
 * the only reason the dialog exists. `autofocus` is not an option: the plugin
 * sets focus after the element is shown, so it would win and take it back.
 */
function initSearchModal() {
	var modal = document.getElementById( 'siteSearch' );

	if ( ! modal ) {
		return;
	}

	modal.addEventListener( 'shown.bs.modal', function () {
		var field = modal.querySelector( 'input[type="search"], .search-field' );

		if ( field ) {
			field.focus();
		}
	} );
}

/**
 * Product gallery: the thumbnail strip drives the stage.
 *
 * Not Bootstrap's carousel — that brings a sliding transition and an indicator
 * row that is not a thumbnail strip. The stage is a stack of images toggled by
 * `data-active`, and the thumbs are a real tablist, which is what the comp marks
 * up too and what keeps the arrow keys working.
 */
function initProductGalleries() {
	document.querySelectorAll( '[data-gallery]' ).forEach( function ( gallery ) {
		var images = Array.prototype.slice.call( gallery.querySelectorAll( '.cmwp-gallery__img' ) );
		var thumbs = Array.prototype.slice.call( gallery.querySelectorAll( '[data-gallery-thumb]' ) );
		var prev = gallery.querySelector( '[data-gallery-prev]' );
		var next = gallery.querySelector( '[data-gallery-next]' );
		var expand = gallery.querySelector( '[data-gallery-expand]' );

		// The lightbox is NOT inside the gallery — it cannot be, because the
		// gallery column is sticky and a sticky element creates a stacking
		// context a modal cannot paint out of. It is found through the expand
		// button's own `data-bs-target`, which is the same string Bootstrap
		// resolves, so the two stay associated without a second selector to keep
		// in step.
		var lightbox = expand ? document.querySelector( expand.getAttribute( 'data-bs-target' ) ) : null;
		var lightboxImg = lightbox ? lightbox.querySelector( '[data-gallery-lightbox-img]' ) : null;

		// The expand button is wired before the two-image guard below: a single
		// image has no arrows and no thumbs, but it can still be opened full
		// size, which is the whole point of the control.
		//
		// The source is read at click time rather than tracked alongside
		// `current`, so it stays right no matter what moved the stage on.
		// srcset/sizes come across too, or the modal would load the column-width
		// candidate and show it upscaled.
		if ( expand && lightboxImg ) {
			var lightboxLabel = lightbox.querySelector( '[data-gallery-lightbox-label]' );
			var lightboxLabelText = lightbox.querySelector( '[data-gallery-lightbox-label-text]' );

			expand.addEventListener( 'click', function () {
				var active = gallery.querySelector( '.cmwp-gallery__img[data-active="true"]' ) || images[ 0 ];

				if ( ! active ) {
					return;
				}

				lightboxImg.setAttribute( 'src', active.currentSrc || active.src );
				lightboxImg.setAttribute( 'alt', active.getAttribute( 'alt' ) || '' );

				if ( active.srcset ) {
					lightboxImg.setAttribute( 'srcset', active.srcset );
					lightboxImg.setAttribute( 'sizes', '100vw' );
				}

				// The comp labels the frame. The only description this gallery
				// has is the attachment's alt text, so an image without one gets
				// no label rather than an empty eyebrow with a dash on it.
				if ( lightboxLabel && lightboxLabelText ) {
					var alt = active.getAttribute( 'alt' ) || '';
					lightboxLabelText.textContent = alt;
					lightboxLabel.classList.toggle( 'd-none', '' === alt );
				}
			} );

			initLightboxZoom( lightbox, lightboxImg );
		}

		initStageZoom( gallery );

		if ( images.length < 2 ) {
			return;
		}

		var current = 0;

		function show( index ) {
			current = ( index + images.length ) % images.length;

			images.forEach( function ( img, i ) {
				img.setAttribute( 'data-active', i === current ? 'true' : 'false' );
			} );

			thumbs.forEach( function ( thumb, i ) {
				thumb.setAttribute( 'aria-selected', i === current ? 'true' : 'false' );
			} );
		}

		thumbs.forEach( function ( thumb, i ) {
			thumb.addEventListener( 'click', function () {
				show( i );
			} );
		} );

		if ( prev ) {
			prev.addEventListener( 'click', function () {
				show( current - 1 );
			} );
		}

		if ( next ) {
			next.addEventListener( 'click', function () {
				show( current + 1 );
			} );
		}

		// Arrow keys move between thumbs, as a tablist should.
		gallery.addEventListener( 'keydown', function ( event ) {
			if ( ! event.target.hasAttribute( 'data-gallery-thumb' ) ) {
				return;
			}

			var delta = event.key === 'ArrowRight' ? 1 : ( event.key === 'ArrowLeft' ? -1 : 0 );

			if ( ! delta ) {
				return;
			}

			event.preventDefault();
			show( current + delta );
			thumbs[ current ].focus();
		} );
	} );
}

/**
 * The minus/plus buttons on a quantity field.
 *
 * The field is WooCommerce's, and it stays WooCommerce's: min, max and step are
 * read off the input rather than off the product a second time, so a
 * sold-individually item or a case-pack step is respected without this knowing
 * what either of those is. The buttons only ever move the value between the
 * bounds the input already declares.
 *
 * `change` is dispatched afterwards because the cart page's own update button
 * listens for it — a value set from script fires no event of its own.
 *
 * Delegated from the document rather than bound per control: the cart drawer and
 * the cart page both re-render their quantities through Woo's AJAX fragments, and
 * a listener bound at DOMContentLoaded would be gone from every replaced node.
 */
function initQuantitySteppers() {
	document.addEventListener( 'click', function ( event ) {
		var button = event.target.closest ? event.target.closest( '[data-qty-step]' ) : null;

		if ( ! button ) {
			return;
		}

		var group = button.closest( '.cmwp-qty' );
		var input = group ? group.querySelector( 'input[type="number"]' ) : null;

		if ( ! input ) {
			return;
		}

		var step = parseFloat( input.getAttribute( 'step' ) ) || 1;
		var min = input.hasAttribute( 'min' ) ? parseFloat( input.getAttribute( 'min' ) ) : 0;
		var max = input.hasAttribute( 'max' ) ? parseFloat( input.getAttribute( 'max' ) ) : Infinity;
		var value = parseFloat( input.value );

		if ( isNaN( value ) ) {
			value = min;
		}

		value += step * parseFloat( button.getAttribute( 'data-qty-step' ) );

		// Clamped rather than left to the input: a number input only enforces its
		// own min/max on form submission, not on a value assigned from script.
		value = Math.min( max, Math.max( min, value ) );

		// Steps can be fractional (weight-priced goods), and float arithmetic on
		// 0.1 does not land on 0.1. Rounded to the step's own precision.
		var places = ( String( step ).split( '.' )[ 1 ] || '' ).length;
		input.value = places ? value.toFixed( places ) : String( value );

		input.dispatchEvent( new Event( 'change', { bubbles: true } ) );
	} );
}

/**
 * Hover zoom on the gallery stage.
 *
 * The comp scales the active image to 1.9 and moves its transform-origin to the
 * pointer, so the stage becomes a magnifier over whichever part of the unit is
 * under the cursor. The stage already clips (`overflow-hidden`), so the picture
 * crops rather than the box growing.
 *
 * Only where the pointer can actually hover. On a touch screen `mousemove` still
 * fires once on tap, which would leave the image stuck at 1.9 with no way to put
 * it back — and the "Hover to zoom" label is hidden there for the same reason.
 * The check is live rather than read once, because a laptop with a touchscreen
 * can be either at any moment.
 *
 * The origin is set on the element rather than tracked in JS state: the browser
 * is already storing it, and reading it back is what keeps the two in step when
 * the stage changes image mid-hover.
 */
function initStageZoom( gallery ) {
	var stage = gallery.querySelector( '[data-gallery-stage]' );
	var hover = window.matchMedia( '(hover: hover) and (pointer: fine)' );

	if ( ! stage ) {
		return;
	}

	function reset() {
		gallery.querySelectorAll( '.cmwp-gallery__img' ).forEach( function ( img ) {
			img.style.transform = '';
			img.style.transformOrigin = '';
		} );
	}

	stage.addEventListener( 'mousemove', function ( event ) {
		var active = gallery.querySelector( '.cmwp-gallery__img[data-active="true"]' );

		if ( ! active || ! hover.matches ) {
			return;
		}

		var box = stage.getBoundingClientRect();

		active.style.transformOrigin =
			( ( event.clientX - box.left ) / box.width * 100 ) + '% ' +
			( ( event.clientY - box.top ) / box.height * 100 ) + '%';
		active.style.transform = 'scale(1.9)';
	} );

	stage.addEventListener( 'mouseleave', reset );

	// A stage that scrolls out from under a held pointer never fires mouseleave,
	// and neither does one whose image is swapped by the arrows mid-hover.
	gallery.addEventListener( 'click', reset );
}

/**
 * Click-to-zoom inside the lightbox.
 *
 * The comp toggles the frame between 1 and 2.1 on click, with the cursor saying
 * which way it goes next, and tracks the pointer only while zoomed. The click
 * target is the FRAME rather than the image: at 2.1 the picture is wider than
 * its box, and an `<img>` target would drop the pointer the moment it moved over
 * the letterboxing beside a portrait shot.
 *
 * Reset on close, or reopening the lightbox would show the last image still
 * magnified at whatever corner it was left in.
 */
function initLightboxZoom( lightbox, img ) {
	var frame = lightbox.querySelector( '[data-gallery-lightbox-frame]' );
	var foot = lightbox.querySelector( '[data-gallery-lightbox-foot]' );
	var zoomed = false;

	if ( ! frame ) {
		return;
	}

	function apply() {
		frame.setAttribute( 'data-zoomed', zoomed ? 'true' : 'false' );
		img.style.transform = zoomed ? 'scale(2.1)' : '';

		if ( ! zoomed ) {
			img.style.transformOrigin = '';
		}

		if ( foot ) {
			foot.textContent = foot.getAttribute( zoomed ? 'data-out' : 'data-in' ) || '';
		}
	}

	frame.addEventListener( 'click', function () {
		zoomed = ! zoomed;
		apply();
	} );

	frame.addEventListener( 'mousemove', function ( event ) {
		if ( ! zoomed ) {
			return;
		}

		var box = frame.getBoundingClientRect();

		img.style.transformOrigin =
			( ( event.clientX - box.left ) / box.width * 100 ) + '% ' +
			( ( event.clientY - box.top ) / box.height * 100 ) + '%';
	} );

	lightbox.addEventListener( 'hidden.bs.modal', function () {
		zoomed = false;
		apply();
	} );
}

/**
 * Reveal the sticky buy bar once the real buy box has scrolled past.
 *
 * An IntersectionObserver on the buy box rather than a scroll listener: the
 * browser does the work off the main thread, and the trigger is "the buy box is
 * no longer on screen", which is what the comp's behaviour actually is.
 *
 * `inert` travels with the visible state, so a hidden bar is never tabbable and
 * never read out while the buy box itself is in view.
 */
function initStickyBuyBars() {
	var bar = document.querySelector( '[data-sticky-bar]' );
	var trigger = document.querySelector( '[data-sticky-trigger]' );

	if ( ! bar || ! trigger || ! ( 'IntersectionObserver' in window ) ) {
		return;
	}

	var observer = new IntersectionObserver( function ( entries ) {
		entries.forEach( function ( entry ) {
			// Only once the buy box has left upwards — passing it going back up
			// the page should hide the bar again, which comparing the bounding
			// rect to the root gives us and `isIntersecting` alone does not.
			var passed = ! entry.isIntersecting && entry.boundingClientRect.top < 0;

			bar.setAttribute( 'data-shown', passed ? 'true' : 'false' );
			bar.setAttribute( 'aria-hidden', passed ? 'false' : 'true' );

			if ( passed ) {
				bar.removeAttribute( 'inert' );
			} else {
				bar.setAttribute( 'inert', '' );
			}
		} );
	}, { threshold: 0 } );

	observer.observe( trigger );
}

/**
 * Submit the catalog's filter and sort forms on change.
 *
 * The forms work without this — every control is a real input and each form has
 * a submit button. What this adds is the comp's behaviour, where changing a
 * facet applies it immediately, and it hides the buttons that only existed to
 * make the no-JavaScript path usable.
 *
 * Deliberately NOT wired to the price sliders' `input` event: that fires on
 * every pixel of a drag and would submit a page load per pixel. Their `change`
 * event fires once, when the thumb is released.
 */
function initCatalogForms() {
	var forms = document.querySelectorAll( '[data-catalog-filters], .catalog__sort' );

	if ( ! forms.length ) {
		return;
	}

	forms.forEach( function ( form ) {
		form.querySelectorAll( '[data-catalog-apply]' ).forEach( function ( button ) {
			button.classList.add( 'd-none' );
		} );

		form.addEventListener( 'change', function ( event ) {
			if ( event.target.matches( 'input[type="search"]' ) ) {
				return;
			}

			form.submit();
		} );
	} );
}

/**
 * The price facet's readout and filled track.
 *
 * Two range inputs share one track, so the pair has to be kept from crossing
 * over and the fill between them has to be drawn — neither of which a range
 * input does on its own, and neither of which can be a server-rendered class,
 * because the values change as the thumb moves.
 *
 * The fill is positioned by setting style properties here rather than with an
 * inline style attribute in the template: this theme writes none (CLAUDE.md 3),
 * and a percentage that changes on drag is not something a stylesheet can hold.
 */
function initPriceRanges() {
	document.querySelectorAll( '[data-price-range]' ).forEach( function ( wrap ) {
		var min = wrap.querySelector( '[data-price-min]' );
		var max = wrap.querySelector( '[data-price-max]' );
		var fill = wrap.querySelector( '[data-price-fill]' );
		var lo = wrap.querySelector( '[data-price-lo]' );
		var hi = wrap.querySelector( '[data-price-hi]' );

		if ( ! min || ! max ) {
			return;
		}

		var floor = parseFloat( min.min );
		var ceiling = parseFloat( min.max );
		var span = ceiling - floor;

		// The server renders the readout already formatted, so the currency
		// symbol, the thousands separator and their order are read back off it
		// rather than hard-coded here — this script never has to know that the
		// site is in dollars.
		var pattern = lo ? lo.textContent.trim() : '';

		function format( value ) {
			var rounded = Math.round( value );

			if ( ! pattern ) {
				return String( rounded );
			}

			return pattern.replace( /[\d.,]+/, function () {
				return rounded.toLocaleString();
			} );
		}

		function draw() {
			// A drag that would take one thumb past the other stops at it, so
			// the pair can meet but never invert.
			if ( parseFloat( min.value ) > parseFloat( max.value ) ) {
				if ( document.activeElement === min ) {
					min.value = max.value;
				} else {
					max.value = min.value;
				}
			}

			var low = ( parseFloat( min.value ) - floor ) / span;
			var high = ( parseFloat( max.value ) - floor ) / span;

			if ( fill ) {
				fill.style.left = ( low * 100 ) + '%';
				fill.style.right = ( ( 1 - high ) * 100 ) + '%';
			}

			if ( lo ) {
				lo.textContent = format( parseFloat( min.value ) );
			}

			if ( hi ) {
				hi.textContent = format( parseFloat( max.value ) );
			}
		}

		if ( span > 0 ) {
			min.addEventListener( 'input', draw );
			max.addEventListener( 'input', draw );
			draw();
		}
	} );
}

/**
 * Open primary-nav submenus on hover/mouseleave at desktop widths, in
 * addition to Bootstrap's default click-to-toggle behaviour (which still
 * drives touch and keyboard interaction, and the mobile accordion).
 *
 * The 992px check matches Bootstrap's default `lg` breakpoint, which is
 * what `.navbar-expand-lg` / `.offcanvas-lg` switch on.
 */
function initNavDropdownHover() {
	if ( typeof bootstrap === 'undefined' ) {
		return;
	}

	var desktopQuery = window.matchMedia( '(min-width: 992px)' );

	document.querySelectorAll( '.site-header .nav-item.dropdown' ).forEach( function ( item ) {
		var toggle = item.querySelector( '[data-bs-toggle="dropdown"]' );

		if ( ! toggle ) {
			return;
		}

		var dropdown  = bootstrap.Dropdown.getOrCreateInstance( toggle );
		var closeTimer = null;

		item.addEventListener( 'mouseenter', function () {
			if ( ! desktopQuery.matches ) {
				return;
			}

			clearTimeout( closeTimer );
			dropdown.show();
		} );

		// A short delay before closing means briefly crossing outside the
		// item while moving the mouse down toward a submenu link doesn't
		// close the menu before it's reached.
		item.addEventListener( 'mouseleave', function () {
			if ( ! desktopQuery.matches ) {
				return;
			}

			closeTimer = setTimeout( function () {
				dropdown.hide();
			}, 250 );
		} );
	} );
}

/**
 * Drive the gallery-rail section: the prev/next arrows, and the image the
 * lightbox opens on.
 *
 * Everything else is already handled without script. The rail scrolls natively
 * (`overflow-auto` plus scroll-snap), each card is a real <button> so the rail
 * is tabbable, and the lightbox is Bootstrap's modal — the backdrop, Escape,
 * click-outside, the body scroll lock, the focus trap and the focus return all
 * come from the plugin. What is left is which image is showing.
 *
 * Scoped per rail rather than per page, so two galleries can share a page.
 */
function initGalleryRails() {
	document.querySelectorAll( '[data-gallery-rail]' ).forEach( function ( rail ) {
		var track = rail.querySelector( '[data-gallery-track]' );
		var cards = Array.prototype.slice.call( rail.querySelectorAll( '[data-gallery-index]' ) );

		if ( ! track || ! cards.length ) {
			return;
		}

		initRailArrows( rail, track, cards );
		initRailLightbox( rail, cards );
	} );
}

/**
 * Wire one rail's prev/next arrows to its scroll container.
 *
 * @param {Element}   rail  The section element.
 * @param {Element}   track The scrolling flex row.
 * @param {Element[]} cards The cards inside it.
 */
function initRailArrows( rail, track, cards ) {
	var prev = rail.querySelector( '[data-gallery-prev]' );
	var next = rail.querySelector( '[data-gallery-next]' );

	if ( ! prev && ! next ) {
		return;
	}

	// A 4px tolerance, because scrollLeft is fractional once the card width is a
	// clamp: at the far end it settles a fraction short of the maximum, which
	// would leave the "next" arrow enabled with nowhere left to go.
	var TOLERANCE = 4;

	function syncArrows() {
		var max = track.scrollWidth - track.clientWidth;

		if ( prev ) {
			prev.disabled = track.scrollLeft <= TOLERANCE;
		}
		if ( next ) {
			next.disabled = track.scrollLeft >= max - TOLERANCE;
		}
	}

	// One card plus one gap per press. Both are measured rather than assumed:
	// the card width is a clamp, so it changes with the viewport, and reading it
	// back from the DOM is the only thing that stays true at every width.
	function step( direction ) {
		var card = cards[ 0 ];
		var gap = parseFloat( window.getComputedStyle( track ).columnGap ) || 0;
		var distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;

		track.scrollBy( {
			left: direction * distance,
			behavior: window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ? 'auto' : 'smooth',
		} );
	}

	if ( prev ) {
		prev.addEventListener( 'click', function () {
			step( -1 );
		} );
	}
	if ( next ) {
		next.addEventListener( 'click', function () {
			step( 1 );
		} );
	}

	track.addEventListener( 'scroll', syncArrows, { passive: true } );
	window.addEventListener( 'resize', syncArrows );
	syncArrows();
}

/**
 * Point one rail's lightbox at whichever card opened it, and let its own arrows
 * move through the set from there.
 *
 * @param {Element}   rail  The section element.
 * @param {Element[]} cards The cards inside it.
 */
function initRailLightbox( rail, cards ) {
	// The modal is a sibling of the section, not a descendant — see the note in
	// gallery-rail.php — so it is found by the selector the section carries.
	var modal = document.querySelector( rail.getAttribute( 'data-gallery-rail' ) );

	if ( ! modal ) {
		return;
	}

	var image = modal.querySelector( '[data-gallery-modal-img]' );

	if ( ! image ) {
		return;
	}

	// The card's own <img> is the source of truth for the file and its alt text.
	//
	// data-full, NOT currentSrc: the card is responsive, so currentSrc is
	// whichever candidate the browser chose for a ~400px card — reading it here
	// put a 600px file on a full-screen dialog. The attribute carries the
	// original, which is what a lightbox is for. currentSrc stays as the
	// fallback for any card rendered without it.
	var slides = cards.map( function ( card ) {
		return card.querySelector( 'img' );
	} );

	var current = 0;

	function show( index ) {
		// Wraps in both directions, so the arrows never dead-end.
		current = ( ( index % slides.length ) + slides.length ) % slides.length;

		var slide = slides[ current ];

		if ( ! slide ) {
			return;
		}

		image.src = slide.getAttribute( 'data-full' ) || slide.currentSrc || slide.src;
		image.alt = slide.alt;

		// Carried across with the file so the dialog reserves the right box:
		// without them a portrait replacing a landscape resizes the image under
		// the pointer as the new file decodes.
		if ( slide.naturalWidth && slide.naturalHeight ) {
			image.width = slide.naturalWidth;
			image.height = slide.naturalHeight;
		}
	}

	// relatedTarget is the element that triggered the modal, which the plugin
	// hands over before the dialog is painted — so the right image is in place
	// for the opening transition rather than swapping in after it.
	modal.addEventListener( 'show.bs.modal', function ( event ) {
		var card = event.relatedTarget;

		if ( card ) {
			show( parseInt( card.getAttribute( 'data-gallery-index' ), 10 ) || 0 );
		}
	} );

	var modalPrev = modal.querySelector( '[data-gallery-modal-prev]' );
	var modalNext = modal.querySelector( '[data-gallery-modal-next]' );

	if ( modalPrev ) {
		modalPrev.addEventListener( 'click', function () {
			show( current - 1 );
		} );
	}
	if ( modalNext ) {
		modalNext.addEventListener( 'click', function () {
			show( current + 1 );
		} );
	}
}

/**
 * Crossfade a hero-video band's two <video> elements so its loop has no seam.
 *
 * Both elements play the same file. The band shows one at a time and fades to
 * the other just before the playing one ends, so the cut back to frame zero
 * happens under the fade instead of on screen. That is the whole trick, and it
 * is why the markup emits two videos rather than one with `loop`.
 *
 * Neither element carries `autoplay`: playback starts here, which is what lets
 * prefers-reduced-motion leave the band on a still first frame — a media query
 * cannot reach an HTML attribute, and a background video that plays anyway is
 * exactly the motion the preference is asking not to see.
 *
 * Scoped per band, so two heroes could share a page.
 */
function initHeroVideos() {
	document.querySelectorAll( '[data-hero-video]' ).forEach( function ( media ) {
		var videos = Array.prototype.slice.call( media.querySelectorAll( 'video' ) );

		if ( videos.length < 2 ) {
			return;
		}

		// The elements hold their first frame either way; this only decides
		// whether anything moves.
		if ( window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
			return;
		}

		initHeroCrossfade( videos );
	} );
}

/**
 * Drive one band's pair of videos.
 *
 * @param {HTMLVideoElement[]} videos The two elements, in document order.
 */
function initHeroCrossfade( videos ) {
	// The overlap: how long the two are both on screen, and therefore how far
	// before the end of a clip the next one starts. The comp's value.
	var FADE_MS = 900;

	var current = 0;
	var timer = 0;

	// muted is what browsers require before they will autoplay at all — it is in
	// the markup as well, and asserted here because a video element that lost it
	// would silently never start. `loop` is deliberately off: it would restart
	// the playing clip under the fade and defeat the whole point.
	videos.forEach( function ( video ) {
		video.muted = true;
		video.loop = false;
	} );

	/**
	 * Fade one element to an opacity, cancelling whatever it was already doing.
	 *
	 * The Web Animations API rather than a CSS transition, because the two
	 * fades have to be started in the same frame and a class swap cannot say
	 * "from wherever you are now" when a fade is interrupted mid-flight.
	 *
	 * @param {HTMLVideoElement} video The element.
	 * @param {number}           to    Target opacity.
	 * @param {number}           ms    Duration.
	 */
	function fadeTo( video, to, ms ) {
		if ( video.cmwpFade ) {
			video.cmwpFade.cancel();
		}

		var from = ( null == video.cmwpOpacity ) ? 1 : video.cmwpOpacity;

		if ( video.animate ) {
			video.cmwpFade = video.animate(
				[ { opacity: from }, { opacity: to } ],
				{ duration: ms, easing: 'linear', fill: 'forwards' }
			);
		} else {
			video.style.opacity = to;
		}

		video.cmwpOpacity = to;
	}

	/** Queue the next swap for one fade-length before the playing clip ends. */
	function scheduleNext() {
		clearTimeout( timer );

		var duration = videos[ current ].duration;

		// Metadata can still be missing on a slow connection; 5s keeps the band
		// moving rather than stalling on the first clip forever.
		var ms = ( ! duration || isNaN( duration ) )
			? 5000
			: Math.max( 200, ( duration - ( FADE_MS / 1000 ) ) * 1000 );

		timer = setTimeout( swap, ms );
	}

	/** Start the other element from frame zero and cross the two over. */
	function swap() {
		var next = current ^ 1;

		try {
			videos[ next ].currentTime = 0;
		} catch ( e ) {} // eslint-disable-line no-empty -- a seek before metadata throws; the next tick retries.

		play( videos[ next ] );
		fadeTo( videos[ next ], 1, FADE_MS );
		fadeTo( videos[ current ], 0, FADE_MS );

		var previous = current;
		current = next;

		// Paused only once it is off screen, so the last frame of the outgoing
		// clip is still moving while it fades.
		setTimeout( function () {
			videos[ previous ].pause();
		}, FADE_MS + 80 );

		scheduleNext();
	}

	/**
	 * play() rejects rather than throws when autoplay is blocked, and an
	 * unhandled rejection is a console error on a page that is working fine.
	 *
	 * @param {HTMLVideoElement} video The element.
	 */
	function play( video ) {
		var promise = video.play();

		if ( promise && promise.catch ) {
			promise.catch( function () {} );
		}
	}

	fadeTo( videos[ 0 ], 1, 1 );
	fadeTo( videos[ 1 ], 0, 1 );

	function start() {
		play( videos[ 0 ] );
		scheduleNext();
	}

	// duration is only readable from HAVE_METADATA up, and scheduleNext needs it.
	if ( videos[ 0 ].readyState >= 1 ) {
		start();
	} else {
		videos[ 0 ].addEventListener( 'loadedmetadata', start, { once: true } );
	}
}
